'use client'

import React, { cloneElement, isValidElement, useEffect, useState, useMemo } from 'react'
import { cn } from '../index'

interface Props {
  text?: string
  className?: string
  shouldAnimate?: boolean
  wordsPerChunk?: number
  fadeDuration?: number
  children?: React.ReactNode
}

const INLINE_ELEMENTS = ['strong', 'em', 'b', 'i', 'code', 'span']

/** Chunking wraps every text node in a span; elements are cloned. Only inline formatting survives that, so block content must render unanimated. */
export const isInlineFormattingOnly = (node: React.ReactNode): boolean => {
  if (typeof node === 'string' || typeof node === 'number') return true
  if (Array.isArray(node)) return node.every(isInlineFormattingOnly)
  if (isValidElement(node)) {
    if (typeof node.type === 'string' && INLINE_ELEMENTS.includes(node.type)) {
      return isInlineFormattingOnly(node.props.children)
    }
  }
  return false
}

const ChunkSpan = React.memo<{
  fadeDuration: number
  children?: React.ReactNode
}>(({ fadeDuration, children }) => {
  const [show, setShow] = useState(false)

  useEffect(() => {
    const raf = requestAnimationFrame(() => setShow(true))
    return () => cancelAnimationFrame(raf)
  }, [])

  return (
    <span
      style={{
        opacity: show ? 1 : 0,
        filter: show ? 'none' : 'blur(0.5px)',
        transition: `opacity ${fadeDuration}ms ease-out, filter ${fadeDuration}ms ease-out`,
      }}
    >
      {children}
    </span>
  )
})

/** Carries the word budget across nodes so chunk boundaries land where a flat walk of the same text would put them. */
type ChunkCursor = { buffer: string }

const chunkAt = (
  node: React.ReactNode,
  path: string,
  cursor: ChunkCursor,
  wordsPerChunk: number,
  fadeDuration: number,
): Array<React.ReactNode> => {
  if (node === null || node === undefined || typeof node === 'boolean') return []

  if (Array.isArray(node)) {
    return node.flatMap((child, index) => chunkAt(child, `${path}.${index}`, cursor, wordsPerChunk, fadeDuration))
  }

  if (isValidElement(node)) {
    const children = node.props.children
    if (children === null || children === undefined) return [cloneElement(node, { key: path })]
    return [cloneElement(node, { key: path }, ...chunkAt(children, path, cursor, wordsPerChunk, fadeDuration))]
  }

  const text = String(node)
  const chunks: Array<React.ReactNode> = []
  let current = ''

  const push = () => {
    chunks.push(
      <ChunkSpan key={`${path}.${chunks.length}`} fadeDuration={fadeDuration}>
        {current}
      </ChunkSpan>,
    )
    current = ''
  }

  for (const char of text) {
    current += char
    cursor.buffer += char

    if (char === ' ' || char === '\n' || char === '\t') {
      if (cursor.buffer.trim().split(/\s+/).length >= wordsPerChunk) {
        cursor.buffer = ''
        push()
      }
    }
  }

  if (current) push()

  return chunks
}

/**
 * Splits a node tree into per-word chunk spans, keeping elements intact around
 * their chunked children. Boundaries fall on the word budget plus a forced
 * boundary at each element edge. Keys come from the tree path, so a node whose
 * position is unchanged keeps its key — and its fade state — across revisions.
 */
export const chunkNodes = (
  node: React.ReactNode,
  wordsPerChunk: number,
  fadeDuration: number,
): Array<React.ReactNode> =>
  // A lone child is index 0 of a one-element array, so its keys survive react-markdown handing it siblings mid-stream.
  chunkAt(Array.isArray(node) ? node : [node], 'root', { buffer: '' }, wordsPerChunk, fadeDuration)

export const FlowingText: React.FC<Props> = ({
  text,
  className,
  shouldAnimate = true,
  wordsPerChunk = 3,
  fadeDuration = 250,
  children,
}) => {
  const content = text || ''
  const hasChildren = children !== null && children !== undefined

  const chunks = useMemo(() => {
    const tokens = content.split(/(\s+)/).filter(Boolean)
    const out: Array<string> = []
    let buf: string[] = []
    let wc = 0

    tokens.forEach((tok) => {
      const isSpace = /^\s+$/.test(tok)
      const hasNewline = /\n/.test(tok)

      const isBulletStart = /^[-*+]\s/.test(tok)
      const isNumberedStart = /^\d+[.)]\s/.test(tok)
      const isListItem = isBulletStart || isNumberedStart

      if (isListItem && buf.length > 0) {
        out.push(buf.join(''))
        buf = []
        wc = 0
      }

      buf.push(tok)
      if (!isSpace) wc++

      if (wc >= wordsPerChunk || (hasNewline && buf.length > 1)) {
        out.push(buf.join(''))
        buf = []
        wc = 0
      }
    })
    if (buf.length) out.push(buf.join(''))
    return out
  }, [content, wordsPerChunk])

  const childChunks = useMemo(
    () => (hasChildren && isInlineFormattingOnly(children) ? chunkNodes(children, wordsPerChunk, fadeDuration) : null),
    [children, hasChildren, wordsPerChunk, fadeDuration],
  )

  if (!shouldAnimate) {
    return (
      <span className={cn('m-0 whitespace-pre-wrap leading-normal', className)}>
        {hasChildren ? children : content}
      </span>
    )
  }

  // Chunking rebuilds the nodes it walks, so anything the inline gate rejects renders as handed over.
  if (hasChildren && childChunks === null) {
    return <span className={cn('m-0 whitespace-pre-wrap leading-normal', className)}>{children}</span>
  }

  return (
    <span className={cn('m-0 whitespace-pre-wrap leading-normal', className)}>
      {hasChildren
        ? childChunks
        : chunks.map((chunk, i) => (
            <ChunkSpan key={i} fadeDuration={fadeDuration}>
              {chunk}
            </ChunkSpan>
          ))}
    </span>
  )
}

export default FlowingText
